import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Shield, AlertTriangle, Check, Loader2, Lock, Eye, Users } from "lucide-react";

const SECTIONS = [
  {
    key: "perm_client_visibility",
    title: "Client visibility",
    icon: Users,
    description: "Controls which clients each agent can see in their workspace.",
    options: [
      { value: "private", label: "Private", description: "Agents see only the clients assigned to them.", recommended: true },
      { value: "shared", label: "Shared", description: "Agents see every client but only names, clubs and positions for clients they're not assigned to." },
      { value: "full", label: "Full", description: "Agents see every client with the full profile, including market value and contact details." },
    ],
  },
  {
    key: "perm_contract_visibility",
    title: "Contract & financial data",
    icon: Lock,
    description: "Controls who sees wage figures, release clauses and bonus amounts on contracts.",
    options: [
      { value: "private", label: "Private", description: "Only the assigned agent (and admins) see wages and contract terms." },
      { value: "admin_only", label: "Admin only", description: "Only admins see financial details. Other agents see contract metadata without amounts.", recommended: true },
      { value: "shared", label: "Shared", description: "All agents see every contract's financial data." },
    ],
  },
  {
    key: "perm_commission_visibility",
    title: "Commission data",
    icon: Eye,
    description: "Controls who sees commission percentages, monthly streams and payment amounts.",
    options: [
      { value: "private", label: "Private", description: "Agents see only commission streams from their own clients." },
      { value: "admin_only", label: "Admin only", description: "Only admins see commission figures. Payroll page is hidden from other agents.", recommended: true },
      { value: "shared", label: "Shared", description: "All agents see every commission figure." },
    ],
  },
];

export default function SettingsPermissions() {
  const [perms, setPerms] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [warnOpen, setWarnOpen] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data));
    api.get("/workspaces/mine/permissions").then((r) => {
      setPerms(r.data);
      setDraft(r.data);
    });
  }, []);

  const isAdmin = me?.role === "admin";
  const dirty = perms && draft && SECTIONS.some((s) => draft[s.key] !== perms[s.key]);

  const setOption = (key, value) => {
    if (!isAdmin) return;
    // Warn when relaxing client_visibility from private → shared/full.
    if (key === "perm_client_visibility" && perms?.[key] === "private" && value !== "private") {
      setDraft({ ...draft, [key]: value });
      setWarnOpen(true);
      return;
    }
    setDraft({ ...draft, [key]: value });
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const payload = {};
      SECTIONS.forEach((s) => {
        if (draft[s.key] !== perms[s.key]) payload[s.key] = draft[s.key];
      });
      const { data } = await api.patch("/workspaces/mine/permissions", payload);
      setPerms(data);
      setDraft(data);
      setToast({ kind: "ok", text: "Permissions updated" });
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setToast({ kind: "err", text: "Failed to save permissions" });
      setTimeout(() => setToast(null), 3500);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setDraft(perms);

  if (!perms || !draft) {
    return (
      <Layout title="Permissions" subtitle="Workspace data visibility">
        <div className="text-sm text-slate-500">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout title="Permissions" subtitle="Workspace data visibility settings">
      <div className="max-w-3xl space-y-5" data-testid="settings-permissions-page">
        {!isAdmin && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="permissions-readonly-banner">
            <Shield size={16} className="text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-900">
              Only the workspace admin can change permissions. You're viewing the current settings in read-only mode.
            </div>
          </div>
        )}

        {SECTIONS.map((s) => (
          <PermissionSection
            key={s.key}
            section={s}
            value={draft[s.key]}
            onChange={(v) => setOption(s.key, v)}
            readonly={!isAdmin}
          />
        ))}

        <div className="flex items-center justify-end gap-2 pt-2">
          {dirty && (
            <button onClick={reset} className="pm-btn-secondary h-10 px-4 rounded-lg text-sm font-bold" data-testid="permissions-reset">
              Discard changes
            </button>
          )}
          <button
            onClick={save}
            disabled={!isAdmin || !dirty || saving}
            className="pm-btn-primary h-10 px-5 rounded-lg text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="permissions-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? "Saving…" : "Save permissions"}
          </button>
        </div>

        {toast && (
          <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold ${toast.kind === "ok" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
            {toast.text}
          </div>
        )}
      </div>

      {warnOpen && (
        <ConfirmModal
          onCancel={() => {
            setDraft({ ...draft, perm_client_visibility: perms.perm_client_visibility });
            setWarnOpen(false);
          }}
          onConfirm={() => setWarnOpen(false)}
        />
      )}
    </Layout>
  );
}

function PermissionSection({ section, value, onChange, readonly }) {
  const Icon = section.icon;
  return (
    <div className="pm-card p-5" data-testid={`permissions-section-${section.key}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-slate-900 text-emerald-400 flex items-center justify-center">
          <Icon size={16} />
        </div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold tracking-tight text-slate-900">{section.title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{section.description}</div>
        </div>
      </div>
      <div className="space-y-2">
        {section.options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => !readonly && onChange(opt.value)}
              disabled={readonly}
              data-testid={`permissions-option-${section.key}-${opt.value}`}
              className={`w-full text-left rounded-xl border p-3 transition-all flex items-start gap-3 ${
                selected
                  ? "border-emerald-500 bg-emerald-50/40 ring-1 ring-emerald-500/30"
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/40"
              } ${readonly ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}
            >
              <span className={`mt-1 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-emerald-600 bg-emerald-600" : "border-slate-300 bg-white"}`}>
                {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-sm text-slate-900">{opt.label}</span>
                  {opt.recommended && <span className="pm-pill pm-pill-green text-[10px]">Recommended default</span>}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">{opt.description}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ConfirmModal({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} data-testid="permissions-warn-modal">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 flex items-start gap-3 border-b border-slate-200">
          <AlertTriangle size={20} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-bold text-base text-slate-900">Make client data visible to your team?</div>
            <div className="text-sm text-slate-600 mt-1">
              This will let every agent in your workspace see client names, clubs and positions — including clients they're not assigned to. You can switch back to Private at any time.
            </div>
          </div>
        </div>
        <div className="px-6 py-4 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onCancel} className="pm-btn-secondary h-9 px-3 rounded-lg text-sm font-bold" data-testid="permissions-warn-cancel">Cancel</button>
          <button onClick={onConfirm} className="pm-btn-primary h-9 px-4 rounded-lg text-sm font-bold" data-testid="permissions-warn-confirm">Yes, change it</button>
        </div>
      </div>
    </div>
  );
}
